import React, {
  useLayoutEffect,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppTab } from './appLayoutConfig';
import { ChartWrapper, TableComponent } from './dsl-components';
import { Spinner } from 'react-bootstrap';
import api from '../../../api';
import './system-manager-wrapper.scss';
import BugKanbanBoard from './BugKanbanBoard';
import RoadmapBoard from './RoadmapBoard';
import {
  GetUnapprovedCompilationsResponse,
  UnapprovedProactivityCompilation,
  GetAgentUseCasesResponse,
  AgentUseCaseSummary,
  GetAgentUseCaseDetailsResponse,
  AgentUseCase,
  AgentUseCaseExpectation,
  CreateAgentUseCaseRequest,
  CreateAgentUseCaseResponse,
  UpdateAgentUseCaseResponse,
  DeleteAgentUseCaseResponse,
  AgentType,
} from '@dreamai/types';

// All available manager types from AgentType enum
const MANAGER_OPTIONS = Object.values(AgentType);

interface CustomerActivityReportResponse {
  success: boolean;
  chartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor: string;
      backgroundColor: string;
      fill: boolean;
      tension: number;
    }[];
  };
  chartOptions: Record<string, unknown>;
  summaryTable: {
    headingArray: string[];
    valuesArrays: string[][];
  };
  dauTable: {
    headingArray: string[];
    valuesArrays: string[][];
  };
  activeUsersTable: {
    headingArray: string[];
    valuesArrays: string[][];
  };
}

interface ActiveUser {
  userId: string;
  userName: string;
  companyId: number;
  companyName: string;
  messageCount: number;
  threadCount: number;
}

interface ActiveUsersResponse {
  success: boolean;
  users: ActiveUser[];
  totalCount: number;
}

interface UserThread {
  threadId: string;
  agentId: string;
  threadDescription: string | null;
  agentNickname: string | null;
  agentType: string;
  updatedAt: string;
  happiness: 'red' | 'amber' | 'green';
  happinessContext: string | null;
  threadSummary: string | null;
  last48hSummary: string | null;
  summaryGeneratedAt: string | null;
}

interface UserThreadsResponse {
  success: boolean;
  threads: UserThread[];
  totalCount: number;
}

interface SystemManagerWrapperProps {
  threadId?: string;
  tabId: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

const SYSTEM_MANAGER_TABS: AppTab[] = [
  { id: 'activity', label: 'Customer Activity' },
  { id: 'threads', label: 'Active Threads' },
  { id: 'bugs', label: 'Bug Tracker' },
  { id: 'roadmap', label: 'Product Roadmap' },
  { id: 'proactivity', label: 'Proactivity Review' },
  { id: 'usecases', label: 'Use Cases' },
];

const SystemManagerWrapper: React.FC<SystemManagerWrapperProps> = ({
  tabId,
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activityData, setActivityData] =
    useState<CustomerActivityReportResponse | null>(null);
  const [activeUsersData, setActiveUsersData] =
    useState<ActiveUsersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);

  // Accordion state - which user is expanded and their threads
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userThreads, setUserThreads] = useState<Map<string, UserThread[]>>(
    new Map(),
  );
  const [loadingThreadsFor, setLoadingThreadsFor] = useState<string | null>(
    null,
  );

  // Proactivity review state
  const [proactivityData, setProactivityData] = useState<
    UnapprovedProactivityCompilation[]
  >([]);
  const [proactivityLoading, setProactivityLoading] = useState(false);
  const [proactivityError, setProactivityError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  // Use cases state
  const [useCasesData, setUseCasesData] = useState<AgentUseCaseSummary[]>([]);
  const [useCasesLoading, setUseCasesLoading] = useState(false);
  const [useCasesError, setUseCasesError] = useState<string | null>(null);
  const [expandedUseCaseSlug, setExpandedUseCaseSlug] = useState<string | null>(
    null,
  );
  const [useCaseDetails, setUseCaseDetails] = useState<
    Map<string, AgentUseCase>
  >(new Map());
  const [loadingDetailsFor, setLoadingDetailsFor] = useState<string | null>(
    null,
  );

  // Use case CRUD state
  const [showUseCaseModal, setShowUseCaseModal] = useState(false);
  const [editingUseCase, setEditingUseCase] = useState<AgentUseCase | null>(
    null,
  );
  const [savingUseCase, setSavingUseCase] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);
  const [useCaseFormData, setUseCaseFormData] = useState<{
    slug: string;
    title: string;
    summary: string;
    description: string;
    limitations: string;
    managers: string[];
    recommendedModes: string[];
    expectations: { title: string; description: string }[];
  }>({
    slug: '',
    title: '',
    summary: '',
    description: '',
    limitations: '',
    managers: [],
    recommendedModes: [],
    expectations: [],
  });

  // Filter state for use cases list
  const [managerFilter, setManagerFilter] = useState<string>('');

  // Notify parent about available tabs synchronously
  useLayoutEffect(() => {
    if (onTabsReady) {
      onTabsReady(SYSTEM_MANAGER_TABS);
    }
  }, [onTabsReady]);

  // Fetch activity data when on activity tab
  useEffect(() => {
    if (tabId !== 'activity') return;

    const fetchActivityData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<CustomerActivityReportResponse>(
          '/customer-activity-report',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setActivityData(response.data);
        } else {
          setError('Failed to load activity data');
        }
      } catch (err) {
        console.error('Error fetching activity data:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load activity data',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchActivityData();
  }, [tabId, getAccessTokenSilently]);

  // Fetch active users when on threads tab
  useEffect(() => {
    if (tabId !== 'threads') return;

    const fetchActiveUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<ActiveUsersResponse>(
          '/customer-activity-report/active-users',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setActiveUsersData(response.data);
        } else {
          setUsersError('Failed to load active users');
        }
      } catch (err) {
        console.error('Error fetching active users:', err);
        setUsersError(
          err instanceof Error ? err.message : 'Failed to load active users',
        );
      } finally {
        setUsersLoading(false);
      }
    };

    fetchActiveUsers();
  }, [tabId, getAccessTokenSilently]);

  // Fetch threads for a user when accordion is expanded
  const fetchUserThreads = useCallback(
    async (userId: string) => {
      if (userThreads.has(userId)) return; // Already loaded

      setLoadingThreadsFor(userId);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<UserThreadsResponse>(
          `/customer-activity-report/user-threads/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setUserThreads((prev) =>
            new Map(prev).set(userId, response.data.threads),
          );
        }
      } catch (err) {
        console.error('Error fetching user threads:', err);
      } finally {
        setLoadingThreadsFor(null);
      }
    },
    [getAccessTokenSilently, userThreads],
  );

  // Handle accordion toggle
  const handleUserClick = useCallback(
    (userId: string) => {
      if (expandedUserId === userId) {
        setExpandedUserId(null);
      } else {
        setExpandedUserId(userId);
        fetchUserThreads(userId);
      }
    },
    [expandedUserId, fetchUserThreads],
  );

  // Fetch proactivity data when on proactivity tab
  useEffect(() => {
    if (tabId !== 'proactivity') return;

    const fetchProactivityData = async () => {
      setProactivityLoading(true);
      setProactivityError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetUnapprovedCompilationsResponse>(
          '/proactivity/compilations/unapproved',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setProactivityData(response.data.compilations);
        } else {
          setProactivityError('Failed to load proactivity data');
        }
      } catch (err) {
        console.error('Error fetching proactivity data:', err);
        setProactivityError(
          err instanceof Error
            ? err.message
            : 'Failed to load proactivity data',
        );
      } finally {
        setProactivityLoading(false);
      }
    };

    fetchProactivityData();
  }, [tabId, getAccessTokenSilently]);

  // Approve a proactivity compilation
  const handleApproveCompilation = useCallback(
    async (compilationId: string) => {
      setApprovingId(compilationId);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.post(
          `/proactivity/compilations/${compilationId}/approve`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          // Remove from list
          setProactivityData((prev) =>
            prev.filter((c) => c.id !== compilationId),
          );
        }
      } catch (err) {
        console.error('Error approving compilation:', err);
      } finally {
        setApprovingId(null);
      }
    },
    [getAccessTokenSilently],
  );

  // Fetch use cases when on usecases tab
  useEffect(() => {
    if (tabId !== 'usecases') return;

    const fetchUseCases = async () => {
      setUseCasesLoading(true);
      setUseCasesError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetAgentUseCasesResponse>(
          '/agent-use-cases',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setUseCasesData(response.data.useCases);
        } else {
          setUseCasesError('Failed to load use cases');
        }
      } catch (err) {
        console.error('Error fetching use cases:', err);
        setUseCasesError(
          err instanceof Error ? err.message : 'Failed to load use cases',
        );
      } finally {
        setUseCasesLoading(false);
      }
    };

    fetchUseCases();
  }, [tabId, getAccessTokenSilently]);

  // Fetch use case details when accordion is expanded
  const fetchUseCaseDetails = useCallback(
    async (slug: string) => {
      if (useCaseDetails.has(slug)) return; // Already loaded

      setLoadingDetailsFor(slug);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetAgentUseCaseDetailsResponse>(
          `/agent-use-cases/${slug}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setUseCaseDetails((prev) =>
            new Map(prev).set(slug, response.data.useCase),
          );
        }
      } catch (err) {
        console.error('Error fetching use case details:', err);
      } finally {
        setLoadingDetailsFor(null);
      }
    },
    [getAccessTokenSilently, useCaseDetails],
  );

  // Handle use case accordion toggle
  const handleUseCaseClick = useCallback(
    (slug: string) => {
      if (expandedUseCaseSlug === slug) {
        setExpandedUseCaseSlug(null);
      } else {
        setExpandedUseCaseSlug(slug);
        fetchUseCaseDetails(slug);
      }
    },
    [expandedUseCaseSlug, fetchUseCaseDetails],
  );

  // Reset form data for new use case
  const resetUseCaseForm = useCallback(() => {
    setUseCaseFormData({
      slug: '',
      title: '',
      summary: '',
      description: '',
      limitations: '',
      managers: [],
      recommendedModes: [],
      expectations: [],
    });
    setEditingUseCase(null);
  }, []);

  // Open modal for creating new use case
  const handleCreateUseCase = useCallback(() => {
    resetUseCaseForm();
    setShowUseCaseModal(true);
  }, [resetUseCaseForm]);

  // Open modal for editing use case
  const handleEditUseCase = useCallback((useCase: AgentUseCase) => {
    setEditingUseCase(useCase);
    setUseCaseFormData({
      slug: useCase.slug,
      title: useCase.title,
      summary: useCase.summary,
      description: useCase.description,
      limitations: useCase.limitations || '',
      managers: useCase.managers.map((m: { name: string }) => m.name),
      recommendedModes: (useCase as any).recommendedModes || [],
      expectations: useCase.expectations
        .filter((e: { active: boolean }) => e.active)
        .map((e: { title: string; description: string }) => ({
          title: e.title,
          description: e.description,
        })),
    });
    setShowUseCaseModal(true);
  }, []);

  // Save use case (create or update)
  const handleSaveUseCase = useCallback(async () => {
    setSavingUseCase(true);

    try {
      const token = await getAccessTokenSilently();
      const managersArray = useCaseFormData.managers;

      if (editingUseCase) {
        // Update existing use case
        const response = await api.put<UpdateAgentUseCaseResponse>(
          `/agent-use-cases/${editingUseCase.slug}`,
          {
            title: useCaseFormData.title,
            summary: useCaseFormData.summary,
            description: useCaseFormData.description,
            limitations: useCaseFormData.limitations || null,
            recommendedModes: useCaseFormData.recommendedModes,
            managers: managersArray,
            expectations: useCaseFormData.expectations.map((e, idx) => ({
              title: e.title,
              description: e.description,
              sortOrder: idx,
              active: true,
            })),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          // Update local state
          setUseCasesData((prev) =>
            prev.map((uc) =>
              uc.slug === editingUseCase.slug
                ? {
                    ...uc,
                    title: response.data.useCase.title,
                    summary: response.data.useCase.summary,
                    managers: response.data.useCase.managers.map((m) => m.name),
                    expectationCount: response.data.useCase.expectations.filter(
                      (e) => e.active,
                    ).length,
                  }
                : uc,
            ),
          );
          setUseCaseDetails((prev) => {
            const updated = new Map(prev);
            updated.set(editingUseCase.slug, response.data.useCase);
            return updated;
          });
          setShowUseCaseModal(false);
          resetUseCaseForm();
        }
      } else {
        // Create new use case
        const response = await api.post<CreateAgentUseCaseResponse>(
          '/agent-use-cases',
          {
            slug: useCaseFormData.slug,
            title: useCaseFormData.title,
            summary: useCaseFormData.summary,
            description: useCaseFormData.description,
            limitations: useCaseFormData.limitations || null,
            recommendedModes: useCaseFormData.recommendedModes,
            managers: managersArray,
            expectations: useCaseFormData.expectations.map((e, idx) => ({
              title: e.title,
              description: e.description,
              sortOrder: idx,
            })),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          // Add to local state
          setUseCasesData((prev) => [
            ...prev,
            {
              id: response.data.useCase.id,
              slug: response.data.useCase.slug,
              title: response.data.useCase.title,
              summary: response.data.useCase.summary,
              active: response.data.useCase.active,
              managers: response.data.useCase.managers.map((m) => m.name),
              expectationCount: response.data.useCase.expectations.filter(
                (e) => e.active,
              ).length,
            },
          ]);
          setShowUseCaseModal(false);
          resetUseCaseForm();
        }
      }
    } catch (err) {
      console.error('Error saving use case:', err);
      alert(
        `Failed to save use case: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setSavingUseCase(false);
    }
  }, [
    getAccessTokenSilently,
    useCaseFormData,
    editingUseCase,
    resetUseCaseForm,
  ]);

  // Delete use case
  const handleDeleteUseCase = useCallback(
    async (slug: string) => {
      if (!confirm(`Are you sure you want to delete this use case?`)) {
        return;
      }

      setDeletingSlug(slug);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.delete<DeleteAgentUseCaseResponse>(
          `/agent-use-cases/${slug}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          // Remove from local state
          setUseCasesData((prev) => prev.filter((uc) => uc.slug !== slug));
          setUseCaseDetails((prev) => {
            const updated = new Map(prev);
            updated.delete(slug);
            return updated;
          });
          if (expandedUseCaseSlug === slug) {
            setExpandedUseCaseSlug(null);
          }
        }
      } catch (err) {
        console.error('Error deleting use case:', err);
        alert(
          `Failed to delete use case: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      } finally {
        setDeletingSlug(null);
      }
    },
    [getAccessTokenSilently, expandedUseCaseSlug],
  );

  // Add expectation to form
  const handleAddExpectation = useCallback(() => {
    setUseCaseFormData((prev) => ({
      ...prev,
      expectations: [...prev.expectations, { title: '', description: '' }],
    }));
  }, []);

  // Remove expectation from form
  const handleRemoveExpectation = useCallback((index: number) => {
    setUseCaseFormData((prev) => ({
      ...prev,
      expectations: prev.expectations.filter((_, i) => i !== index),
    }));
  }, []);

  // Update expectation in form
  const handleUpdateExpectation = useCallback(
    (index: number, field: 'title' | 'description', value: string) => {
      setUseCaseFormData((prev) => ({
        ...prev,
        expectations: prev.expectations.map((e, i) =>
          i === index ? { ...e, [field]: value } : e,
        ),
      }));
    },
    [],
  );

  // Render the appropriate component based on selected tab
  const renderTabContent = () => {
    switch (tabId) {
      case 'activity':
        if (loading) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading customer activity data...</p>
            </div>
          );
        }

        if (error) {
          return (
            <div className='activity-error'>
              <p>{error}</p>
            </div>
          );
        }

        if (!activityData) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading...</p>
            </div>
          );
        }

        return (
          <div className='activity-content'>
            <h2>Customer Activity Report</h2>

            <div className='chart-section'>
              <ChartWrapper
                type='line'
                data={activityData.chartData}
                options={activityData.chartOptions}
              />
            </div>

            <div className='tables-section'>
              <div className='table-group'>
                <h3>Summary</h3>
                <TableComponent
                  headingArray={activityData.summaryTable.headingArray}
                  valuesArrays={activityData.summaryTable.valuesArrays}
                />
              </div>

              <div className='table-group'>
                <h3>Daily Active Users (Last 7 Days)</h3>
                <TableComponent
                  headingArray={activityData.dauTable.headingArray}
                  valuesArrays={activityData.dauTable.valuesArrays}
                />
              </div>
            </div>

            <div className='active-users-section'>
              <h3>Active Users Yesterday (Top 10)</h3>
              <TableComponent
                headingArray={activityData.activeUsersTable.headingArray}
                valuesArrays={activityData.activeUsersTable.valuesArrays}
              />
            </div>
          </div>
        );
      case 'threads':
        if (usersLoading) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading active users...</p>
            </div>
          );
        }

        if (usersError) {
          return (
            <div className='activity-error'>
              <p>{usersError}</p>
            </div>
          );
        }

        if (!activeUsersData) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading...</p>
            </div>
          );
        }

        return (
          <div className='active-threads-content'>
            <h2>Active Users (Last 48 Hours)</h2>
            <p className='threads-subtitle'>
              {activeUsersData.totalCount} users with recent activity
            </p>

            <div className='users-accordion'>
              {activeUsersData.users.map((user) => {
                const isExpanded = expandedUserId === user.userId;
                const threads = userThreads.get(user.userId);
                const isLoadingThreads = loadingThreadsFor === user.userId;

                return (
                  <div key={user.userId} className='user-accordion-item'>
                    <button
                      className={`user-accordion-header ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => handleUserClick(user.userId)}
                    >
                      <div className='user-info'>
                        <span className='user-name'>{user.userName}</span>
                        <span className='company-name'>{user.companyName}</span>
                      </div>
                      <div className='user-stats'>
                        <span className='stat'>
                          {user.messageCount} messages
                        </span>
                        <span className='stat'>{user.threadCount} threads</span>
                      </div>
                      <span className='expand-icon'>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className='user-threads-panel'>
                        {isLoadingThreads && (
                          <div className='threads-loading'>
                            <Spinner animation='border' size='sm' />
                            <span>Analyzing threads...</span>
                          </div>
                        )}

                        {!isLoadingThreads && threads && threads.length > 0 && (
                          <div className='threads-list'>
                            {threads.map((thread) => (
                              <div
                                key={thread.threadId}
                                className={`thread-card happiness-${thread.happiness}`}
                              >
                                <div className='thread-header'>
                                  <div
                                    className={`happiness-indicator ${thread.happiness}`}
                                    title={
                                      thread.happinessContext ||
                                      'No context available'
                                    }
                                  >
                                    {thread.happiness === 'red' && '🔴'}
                                    {thread.happiness === 'amber' && '🟡'}
                                    {thread.happiness === 'green' && '🟢'}
                                  </div>
                                  <div className='thread-title'>
                                    {thread.threadDescription ||
                                      'Untitled thread'}
                                  </div>
                                  <div className='agent-info'>
                                    {thread.agentNickname
                                      ? `${thread.agentNickname} - Your Business`
                                      : thread.agentType}
                                  </div>
                                </div>

                                {thread.last48hSummary && (
                                  <div className='recent-activity'>
                                    <strong>Recent:</strong>{' '}
                                    {thread.last48hSummary}
                                  </div>
                                )}

                                {thread.happinessContext && (
                                  <div className='happiness-context'>
                                    <strong>Assessment:</strong>{' '}
                                    {thread.happinessContext}
                                  </div>
                                )}

                                <div className='thread-footer'>
                                  <span className='updated-at'>
                                    {new Date(thread.updatedAt).toLocaleString(
                                      'en-GB',
                                      {
                                        day: 'numeric',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      },
                                    )}
                                  </span>
                                  <a
                                    href={`/conversations?agent_id=${thread.agentId}&thread_id=${thread.threadId}&app_tab=threads`}
                                    className='view-thread-btn'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    View →
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {!isLoadingThreads &&
                          threads &&
                          threads.length === 0 && (
                            <div className='no-threads'>
                              <p>No active threads for this user</p>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}

              {activeUsersData.users.length === 0 && (
                <div className='no-threads'>
                  <p>No active users in the last 48 hours</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'bugs':
        return <BugKanbanBoard />;
      case 'roadmap':
        return <RoadmapBoard />;
      case 'proactivity':
        if (proactivityLoading) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading proactivity compilations...</p>
            </div>
          );
        }

        if (proactivityError) {
          return (
            <div className='activity-error'>
              <p>{proactivityError}</p>
            </div>
          );
        }

        return (
          <div className='proactivity-content'>
            <h2>Proactivity Review</h2>
            <p className='proactivity-subtitle'>
              {proactivityData.length} compilation
              {proactivityData.length !== 1 ? 's' : ''} awaiting approval
            </p>

            {proactivityData.length === 0 ? (
              <div className='no-compilations'>
                <p>No compilations pending approval</p>
              </div>
            ) : (
              <div className='compilations-list'>
                {proactivityData.map((compilation) => (
                  <div key={compilation.id} className='compilation-card'>
                    <div className='compilation-header'>
                      <div className='compilation-title'>
                        {compilation.title}
                      </div>
                      <div className='compilation-meta'>
                        <span className='user-name'>
                          {compilation.userName || 'Unknown User'}
                        </span>
                        <span className='company-name'>
                          {compilation.companyName || 'Unknown Company'}
                        </span>
                      </div>
                    </div>

                    <div className='compilation-body'>
                      <div className='compilation-subtitle'>
                        {compilation.subtitle}
                      </div>
                      {compilation.synthesizedPostChats && (
                        <div className='compilation-summary'>
                          {compilation.synthesizedPostChats}
                        </div>
                      )}
                      <div className='compilation-stats'>
                        <span>{compilation.itemsCount || 0} items</span>
                        {compilation.compiledAt && (
                          <span>
                            {new Date(compilation.compiledAt).toLocaleString(
                              'en-GB',
                              {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className='compilation-actions'>
                      <button
                        className='approve-btn'
                        onClick={() => handleApproveCompilation(compilation.id)}
                        disabled={approvingId === compilation.id}
                      >
                        {approvingId === compilation.id
                          ? 'Approving...'
                          : 'Approve'}
                      </button>
                      {compilation.threadId && (
                        <a
                          href={`/conversations?thread_id=${compilation.threadId}`}
                          className='view-thread-btn'
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          View Thread →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'usecases':
        if (useCasesLoading) {
          return (
            <div className='activity-loading'>
              <Spinner animation='border' size='sm' />
              <p>Loading use cases...</p>
            </div>
          );
        }

        if (useCasesError) {
          return (
            <div className='activity-error'>
              <p>{useCasesError}</p>
            </div>
          );
        }

        return (
          <div className='usecases-content'>
            <div className='usecases-header'>
              <div>
                <h2>Agent Use Cases</h2>
                <p className='usecases-subtitle'>
                  {useCasesData.length} use case
                  {useCasesData.length !== 1 ? 's' : ''} defined
                </p>
              </div>
              <div className='usecases-header-actions'>
                <div className='manager-filter'>
                  <label>Filter by Manager:</label>
                  <select
                    value={managerFilter}
                    onChange={(e) => setManagerFilter(e.target.value)}
                  >
                    <option value=''>All Managers</option>
                    {MANAGER_OPTIONS.map((manager) => (
                      <option key={manager} value={manager}>
                        {manager}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className='create-usecase-btn'
                  onClick={handleCreateUseCase}
                >
                  + New Use Case
                </button>
              </div>
            </div>

            {/* Use Case Modal */}
            {showUseCaseModal && (
              <div className='usecase-modal-overlay'>
                <div className='usecase-modal'>
                  <div className='usecase-modal-header'>
                    <h3>
                      {editingUseCase ? 'Edit Use Case' : 'Create Use Case'}
                    </h3>
                    <button
                      className='modal-close-btn'
                      onClick={() => {
                        setShowUseCaseModal(false);
                        resetUseCaseForm();
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div className='usecase-modal-body'>
                    <div className='form-group'>
                      <label>Slug (unique identifier)</label>
                      <input
                        type='text'
                        value={useCaseFormData.slug}
                        onChange={(e) =>
                          setUseCaseFormData((prev) => ({
                            ...prev,
                            slug: e.target.value,
                          }))
                        }
                        disabled={!!editingUseCase}
                        placeholder='e.g., send-emails'
                      />
                    </div>

                    <div className='form-group'>
                      <label>Title</label>
                      <input
                        type='text'
                        value={useCaseFormData.title}
                        onChange={(e) =>
                          setUseCaseFormData((prev) => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        placeholder='e.g., Send Emails'
                      />
                    </div>

                    <div className='form-group'>
                      <label>Summary</label>
                      <textarea
                        value={useCaseFormData.summary}
                        onChange={(e) =>
                          setUseCaseFormData((prev) => ({
                            ...prev,
                            summary: e.target.value,
                          }))
                        }
                        rows={2}
                        placeholder='Brief summary of the use case'
                      />
                    </div>

                    <div className='form-group'>
                      <label>Description</label>
                      <textarea
                        value={useCaseFormData.description}
                        onChange={(e) =>
                          setUseCaseFormData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        style={{ minHeight: '400px' }}
                        placeholder='Detailed description'
                      />
                    </div>

                    <div className='form-group'>
                      <label>Limitations (optional)</label>
                      <textarea
                        value={useCaseFormData.limitations}
                        onChange={(e) =>
                          setUseCaseFormData((prev) => ({
                            ...prev,
                            limitations: e.target.value,
                          }))
                        }
                        rows={2}
                        placeholder='Any limitations or caveats'
                      />
                    </div>

                    <div className='form-group managers-section'>
                      <label>Managers</label>
                      <div className='managers-checkboxes'>
                        {MANAGER_OPTIONS.map((manager) => (
                          <label key={manager} className='manager-checkbox'>
                            <input
                              type='checkbox'
                              checked={useCaseFormData.managers.includes(
                                manager,
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setUseCaseFormData((prev) => ({
                                    ...prev,
                                    managers: [...prev.managers, manager],
                                  }));
                                } else {
                                  setUseCaseFormData((prev) => ({
                                    ...prev,
                                    managers: prev.managers.filter(
                                      (m) => m !== manager,
                                    ),
                                  }));
                                }
                              }}
                            />
                            <span>{manager}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className='form-group recommended-modes-section'>
                      <label>
                        Recommended Modes
                        <button
                          type='button'
                          className='add-expectation-btn'
                          onClick={() => {
                            const mode = prompt(
                              'Enter mode name (e.g., email, calendar, web-research):',
                            );
                            if (mode?.trim()) {
                              setUseCaseFormData((prev) => ({
                                ...prev,
                                recommendedModes: [
                                  ...prev.recommendedModes,
                                  mode.trim(),
                                ],
                              }));
                            }
                          }}
                        >
                          + Add
                        </button>
                      </label>
                      <div className='recommended-modes-form-list'>
                        {useCaseFormData.recommendedModes.map((mode, idx) => (
                          <span key={idx} className='mode-tag'>
                            {mode}
                            <button
                              type='button'
                              className='remove-mode-btn'
                              onClick={() =>
                                setUseCaseFormData((prev) => ({
                                  ...prev,
                                  recommendedModes:
                                    prev.recommendedModes.filter(
                                      (_, i) => i !== idx,
                                    ),
                                }))
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {useCaseFormData.recommendedModes.length === 0 && (
                          <span className='no-modes-text'>No modes added</span>
                        )}
                      </div>
                    </div>

                    <div className='form-group expectations-section'>
                      <label>
                        Expectations
                        <button
                          type='button'
                          className='add-expectation-btn'
                          onClick={handleAddExpectation}
                        >
                          + Add
                        </button>
                      </label>
                      {useCaseFormData.expectations.map((exp, idx) => (
                        <div key={idx} className='expectation-form-item'>
                          <input
                            type='text'
                            value={exp.title}
                            onChange={(e) =>
                              handleUpdateExpectation(
                                idx,
                                'title',
                                e.target.value,
                              )
                            }
                            placeholder='Expectation title'
                          />
                          <textarea
                            value={exp.description}
                            onChange={(e) =>
                              handleUpdateExpectation(
                                idx,
                                'description',
                                e.target.value,
                              )
                            }
                            rows={2}
                            placeholder='Expectation description'
                          />
                          <button
                            type='button'
                            className='remove-expectation-btn'
                            onClick={() => handleRemoveExpectation(idx)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='usecase-modal-footer'>
                    <button
                      className='modal-cancel-btn'
                      onClick={() => {
                        setShowUseCaseModal(false);
                        resetUseCaseForm();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className='modal-save-btn'
                      onClick={handleSaveUseCase}
                      disabled={savingUseCase}
                    >
                      {savingUseCase
                        ? 'Saving...'
                        : editingUseCase
                          ? 'Update'
                          : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {useCasesData.length === 0 ? (
              <div className='no-usecases'>
                <p>No use cases defined yet</p>
                <button
                  className='create-usecase-btn'
                  onClick={handleCreateUseCase}
                >
                  Create your first use case
                </button>
              </div>
            ) : (
              <div className='usecases-accordion'>
                {useCasesData
                  .filter(
                    (useCase) =>
                      !managerFilter ||
                      useCase.managers.includes(managerFilter),
                  )
                  .map((useCase) => {
                    const isExpanded = expandedUseCaseSlug === useCase.slug;
                    const details = useCaseDetails.get(useCase.slug);
                    const isLoadingDetails = loadingDetailsFor === useCase.slug;

                    return (
                      <div
                        key={useCase.slug}
                        className='usecase-accordion-item'
                      >
                        <button
                          className={`usecase-accordion-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => handleUseCaseClick(useCase.slug)}
                        >
                          <div className='usecase-info'>
                            <span className='usecase-title'>
                              {useCase.title}
                            </span>
                            <span className='usecase-slug'>{useCase.slug}</span>
                          </div>
                          <div className='usecase-meta'>
                            <span className='managers-badge'>
                              {useCase.managers.join(', ')}
                            </span>
                            <span className='expectations-count'>
                              {useCase.expectationCount} expectation
                              {useCase.expectationCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <span className='expand-icon'>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </button>

                        {isExpanded && (
                          <div className='usecase-details-panel'>
                            {isLoadingDetails && (
                              <div className='details-loading'>
                                <Spinner animation='border' size='sm' />
                                <span>Loading details...</span>
                              </div>
                            )}

                            {!isLoadingDetails && details && (
                              <div className='usecase-details'>
                                <div className='detail-section'>
                                  <h4>Summary</h4>
                                  <p>{details.summary}</p>
                                </div>

                                <div className='detail-section'>
                                  <h4>Description</h4>
                                  <p>{details.description}</p>
                                </div>

                                {details.limitations && (
                                  <div className='detail-section limitations'>
                                    <h4>Limitations</h4>
                                    <p>{details.limitations}</p>
                                  </div>
                                )}

                                {(details as any).recommendedModes?.length >
                                  0 && (
                                  <div className='detail-section'>
                                    <h4>Recommended Modes</h4>
                                    <div className='recommended-modes-list'>
                                      {(details as any).recommendedModes.map(
                                        (mode: string, idx: number) => (
                                          <span
                                            key={idx}
                                            className='mode-badge'
                                          >
                                            {mode}
                                          </span>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}

                                {details.expectations.length > 0 && (
                                  <div className='detail-section'>
                                    <h4>Expectations</h4>
                                    <ul className='expectations-list'>
                                      {details.expectations
                                        .filter((e) => e.active)
                                        .map((exp, idx) => (
                                          <li
                                            key={idx}
                                            className='expectation-item'
                                          >
                                            <strong>{exp.title}</strong>
                                            <p>{exp.description}</p>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                )}

                                <div className='usecase-footer'>
                                  <span
                                    className={`status-badge ${details.active ? 'active' : 'inactive'}`}
                                  >
                                    {details.active ? 'Active' : 'Inactive'}
                                  </span>
                                  <div className='usecase-actions'>
                                    <button
                                      className='edit-usecase-btn'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditUseCase(details);
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className='delete-usecase-btn'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteUseCase(details.slug);
                                      }}
                                      disabled={deletingSlug === details.slug}
                                    >
                                      {deletingSlug === details.slug
                                        ? 'Deleting...'
                                        : 'Delete'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return <div className='system-manager-wrapper'>{renderTabContent()}</div>;
};

export default SystemManagerWrapper;
